import { Box, Button, Card, Chip, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { AvailableSubscriptionPlanResponse, getApiResponseMessage } from '../../../services/subscriptionPaymentService';
import ConfirmDialog from '../../common/ConfirmDialog';
import { enqueueSnackbar } from '../../../store/app-slice/app';
import {
    createSubscriptionCheckoutSession,
    openCustomerPortalSession,
    selectEffectiveLocalSubscriptionId,
    selectHasAnyActiveSubscription,
} from '../../../store/subscription-slice/subscription-slice';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { formatSubscriptionDate, formatSubscriptionPrice } from '../../../utils/subscriptionFormatting';

const TRIAL_DURATION_ONE_WEEK = '1_week';
const TRIAL_DURATION_TWO_WEEKS = '2_weeks';
const TRIAL_DURATION_ONE_MONTH = '1_month';

const formatModelsAllowed = (modelsAllowed: number | null | undefined, fallback: string) => {
    if (modelsAllowed == null) {
        return fallback;
    }

    return modelsAllowed.toString();
};

const formatTrialDuration = (duration: string | undefined | null, t: (value: string) => string) => {
    if (duration === TRIAL_DURATION_ONE_WEEK) return t('TIER_MANAGEMENT_TRIAL_DURATION_1_WEEK');
    if (duration === TRIAL_DURATION_TWO_WEEKS) return t('TIER_MANAGEMENT_TRIAL_DURATION_2_WEEKS');
    if (duration === TRIAL_DURATION_ONE_MONTH) return t('TIER_MANAGEMENT_TRIAL_DURATION_1_MONTH');
    return t('TIER_MANAGEMENT_TRIAL_DURATION_1_MONTH');
};

const formatPlanPrice = (plan: AvailableSubscriptionPlanResponse, fallback: string) => {
    return formatSubscriptionPrice(plan.unitAmount, plan.currency) ?? fallback;
};

const isDateBeforeToday = (value: string | null | undefined) => {
    if (!value) {
        return false;
    }

    const date = moment(value);
    if (!date.isValid()) {
        return false;
    }

    return date.isBefore(moment().startOf('day'));
};

interface PlansSectionProps {
    hidePortalCard?: boolean;
    compactTopPadding?: boolean;
}

type PlansUiAction = { type: 'portal' } | { type: 'plan'; planId: string };

export function PlansSection({ hidePortalCard = false, compactTopPadding = false }: PlansSectionProps) {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { availablePlans, loadingPlans, openingPortal, subscriptionActionInProgress, billingState } = useAppSelector((state) => state.subscription);
    const userInfo = useAppSelector((state) => state.profile.userInfo);
    const hasAnyActiveSubscription = !!useAppSelector(selectHasAnyActiveSubscription);
    const effectiveLocalSubscriptionId = useAppSelector(selectEffectiveLocalSubscriptionId);

    const activeLocalSubscriptionName = userInfo?.currentSubscription?.isActiveSubscription === true ? userInfo.currentSubscription.subscriptionName : null;
    const effectiveSubscriptionName = billingState?.subscriptionName || activeLocalSubscriptionName || null;
    const hasActiveTrialSubscription = billingState?.hasActiveTrialSubscription === true;
    const effectiveSubscriptionEndDate = billingState?.endDateUtc || userInfo?.currentSubscription?.endDate || null;
    const hasExpiredSubscriptionEndDate = isDateBeforeToday(effectiveSubscriptionEndDate);
    const hasEffectiveActiveSubscription = hasAnyActiveSubscription && !hasExpiredSubscriptionEndDate;
    const pendingTierPriceChange = billingState?.pendingTierPriceChange || null;
    const pendingTierPriceChangeAppliesOnDateLabel = formatSubscriptionDate(pendingTierPriceChange?.appliesOnUtc);
    const pendingTierPriceChangeAppliesInDays = typeof pendingTierPriceChange?.appliesInDays === 'number' && Number.isFinite(pendingTierPriceChange.appliesInDays)
        ? Math.max(0, pendingTierPriceChange.appliesInDays)
        : 0;
    const shouldShowPendingTierPriceChange =
        !!pendingTierPriceChange &&
        !!pendingTierPriceChange.currentStripePriceId &&
        !!pendingTierPriceChange.nextStripePriceId &&
        pendingTierPriceChange.currentStripePriceId !== pendingTierPriceChange.nextStripePriceId;

    const [confirmDialog, setConfirmDialog] = useState<{
        open: boolean;
        titleKey: string;
        bodyKey: string;
        bodyParams: Record<string, string>;
        pendingPlan: AvailableSubscriptionPlanResponse | null;
        isChangeAction: boolean;
    }>({ open: false, titleKey: '', bodyKey: '', bodyParams: {}, pendingPlan: null, isChangeAction: false });
    const [uiAction, setUiAction] = useState<PlansUiAction | null>(null);
    const isSubscriptionUiBusy = uiAction !== null || openingPortal || subscriptionActionInProgress !== null;

    const closeConfirmDialog = () => setConfirmDialog((prev) => ({ ...prev, open: false }));

    const sendFeedback = (messageKey: string, isError = false) => {
        dispatch(enqueueSnackbar({
            message: t(messageKey),
            options: { variant: isError ? 'error' : 'success' },
        }));
    };

    const getReturnUrl = () => `${window.location.origin}${window.location.pathname}${window.location.search}`;

    const handleOpenCustomerPortal = async (trackPortalButton = true) => {
        if (trackPortalButton) {
            setUiAction({ type: 'portal' });
        }

        const result = await dispatch(openCustomerPortalSession({ returnUrl: getReturnUrl() }));
        if (openCustomerPortalSession.rejected.match(result)) {
            sendFeedback(result.payload || getApiResponseMessage(null), true);
            setUiAction(null);
            return;
        }

        window.location.assign(result.payload);
    };

    const handleSelectPlan = (plan: AvailableSubscriptionPlanResponse, isCurrentPlan: boolean) => {
        if (!plan?.stripePriceId || isSubscriptionUiBusy || isCurrentPlan) return;

        const rawPlanName = plan.displayName || plan.subscriptionName || '';
        const planNameLabel = rawPlanName ? t(rawPlanName) : '-';
        const isChangeAction = hasEffectiveActiveSubscription && !!effectiveLocalSubscriptionId;

        setConfirmDialog({
            open: true,
            titleKey: isChangeAction ? 'PROFILE_CONFIRM_CHANGE_TITLE' : 'PROFILE_CONFIRM_BUY_TITLE',
            bodyKey: isChangeAction ? 'PROFILE_CONFIRM_CHANGE_BODY' : 'PROFILE_CONFIRM_BUY_BODY',
            bodyParams: { planName: planNameLabel },
            pendingPlan: plan,
            isChangeAction,
        });
    };

    const handleConfirmPlanDialog = async () => {
        closeConfirmDialog();
        const { pendingPlan, isChangeAction } = confirmDialog;
        if (!pendingPlan) return;

        setUiAction({ type: 'plan', planId: pendingPlan.stripePriceId });

        if (isChangeAction) {
            await handleOpenCustomerPortal(false);
            return;
        }

        const rawPlanName = pendingPlan.displayName || pendingPlan.subscriptionName || '';
        const planNameLabel = rawPlanName ? t(rawPlanName) : '-';

        const result = await dispatch(createSubscriptionCheckoutSession({
            returnUrl: getReturnUrl(),
            stripePriceId: pendingPlan.stripePriceId,
            subscriptionName: planNameLabel,
        }));

        if (createSubscriptionCheckoutSession.rejected.match(result)) {
            sendFeedback(result.payload || getApiResponseMessage(null), true);
            setUiAction(null);
            return;
        }

        window.location.assign(result.payload);
    };

    return (
        <>
        <Card
            variant="outlined"
            sx={{
                borderColor: 'var(--selected-table-row-color)',
                backgroundColor: 'background.paper',
                overflow: 'hidden',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
            }}
        >
            <Box sx={{ p: { xs: 1, md: 1.5 } }}>
                {!hidePortalCard && hasEffectiveActiveSubscription && (
                    <Card
                        variant="outlined"
                        sx={{
                            mb: 2,
                            borderRadius: 3,
                            borderColor: 'var(--selected-table-row-color)',
                            background: 'linear-gradient(130deg, var(--card-header-background-color) 0%, var(--button-background-color) 100%)',
                            overflow: 'hidden',
                            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                        }}
                    >
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            alignItems={{ xs: 'flex-start', md: 'center' }}
                            justifyContent="space-between"
                            spacing={2}
                            sx={{ p: { xs: 1, md: 1.5 } }}
                        >
                            <Stack spacing={0.8}>
                                <Typography variant="body1" sx={{ fontWeight: 700, color: 'var(--card-header-text-color)', fontSize: '1rem' }}>
                                    {t('PROFILE_OPEN_STRIPE_CUSTOMER_PORTAL')}
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'var(--card-header-text-color)', opacity: 0.95, fontSize: '0.875rem' }}>
                                    {t('PROFILE_BILLING_PORTAL_DESCRIPTION')}
                                </Typography>
                            </Stack>
                            <Button
                                variant="outlined"
                                disabled={isSubscriptionUiBusy}
                                onClick={() => { void handleOpenCustomerPortal(true); }}
                                sx={{
                                    borderColor: 'var(--card-header-text-color)',
                                    color: 'var(--card-header-text-color)',
                                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                                    fontWeight: 600,
                                    minHeight: 40,
                                    borderWidth: 1.5,
                                    borderRadius: 2,
                                    textTransform: 'none',
                                    fontSize: '0.9rem',
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        borderColor: 'var(--card-header-text-color)',
                                        backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        borderWidth: 1.5,
                                    },
                                    '&.Mui-disabled': {
                                        borderColor: 'rgba(255, 255, 255, 0.35) !important',
                                        color: 'rgba(255, 255, 255, 0.55) !important',
                                        backgroundColor: 'rgba(255, 255, 255, 0.05) !important',
                                        opacity: 0.65,
                                        cursor: 'not-allowed',
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                {uiAction?.type === 'portal'
                                    ? <CircularProgress size={20} color="inherit" />
                                    : t('PROFILE_OPEN_STRIPE_CUSTOMER_PORTAL')
                                }
                            </Button>
                        </Stack>
                    </Card>
                )}

                <Typography variant="body2" sx={{ mb: 2, color: 'var(--link-text-color)', fontSize: '0.9rem' }}>
                    {t('PROFILE_BILLING_DESCRIPTION')}
                </Typography>

                {loadingPlans ? (
                    <Stack spacing={1.8} alignItems="center" sx={{ py: 5 }}>
                        <CircularProgress size={36} sx={{ color: 'var(--button-background-color)' }} />
                        <Typography sx={{ color: 'var(--link-text-color)', fontWeight: 600 }}>
                            {t('PROFILE_LOADING_SUBSCRIPTION_DETAILS')}
                        </Typography>
                    </Stack>
                ) : availablePlans.length === 0 ? (
                    <Box
                        sx={{
                            py: 6,
                            px: 3,
                            textAlign: 'center',
                            borderRadius: 3,
                            border: '1px dashed var(--selected-table-row-color)',
                            backgroundColor: 'action.hover',
                        }}
                    >
                        <Typography sx={{ color: 'var(--link-text-color)', fontWeight: 500, fontSize: '0.95rem' }}>
                            {t('PROFILE_NO_AVAILABLE_PLANS')}
                        </Typography>
                    </Box>
                ) : (
                    <Grid container spacing={2.5} alignItems="stretch">
                        {availablePlans.map((plan: AvailableSubscriptionPlanResponse, index: number) => {
                            const isCurrentPlan = hasEffectiveActiveSubscription && effectiveSubscriptionName === plan.subscriptionName;
                            const planName = plan.displayName || plan.subscriptionName;
                            const intervalLabel = plan.billingInterval ? t(plan.billingInterval) : '-';
                            const rowKey = plan.stripePriceId || `${plan.subscriptionName || 'plan'}-${index}`;
                            const canChangeInStripe = hasEffectiveActiveSubscription && !isCurrentPlan && !!effectiveLocalSubscriptionId;
                            const canBuyInStripe = !hasEffectiveActiveSubscription && !isCurrentPlan;
                            const canManageInStripe = canChangeInStripe || canBuyInStripe;
                            const isTrialPlan = plan.trialSupported === true;
                            const trialAvailableForUser = plan.trialAvailableForUser === true;
                            const showTrialBadge = isTrialPlan && trialAvailableForUser;
                            const showTrialDuration = isTrialPlan && trialAvailableForUser;
                            const showTrialWarning = isTrialPlan && hasActiveTrialSubscription && isCurrentPlan;
                            const showAnyTrialInfo = showTrialBadge || showTrialDuration || showTrialWarning;
                            const isPendingCurrentPricePlan = shouldShowPendingTierPriceChange && plan.stripePriceId === pendingTierPriceChange?.currentStripePriceId;
                            const isPendingNextPricePlan = shouldShowPendingTierPriceChange && plan.stripePriceId === pendingTierPriceChange?.nextStripePriceId;

                            return (
                                <Grid item xs={12} md={3} key={rowKey} sx={{ display: 'flex', alignSelf: 'stretch' }}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            p: { xs: 1.75, sm: 2 },
                                            width: '100%',
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            borderRadius: 3,
                                            borderWidth: isCurrentPlan ? 2 : 1,
                                            borderColor: isCurrentPlan ? 'var(--button-background-color)' : 'var(--selected-table-row-color)',
                                            backgroundColor: 'background.paper',
                                            boxShadow: isCurrentPlan 
                                                ? '0 4px 12px rgba(0, 0, 0, 0.1)' 
                                                : '0 1px 4px rgba(0, 0, 0, 0.06)',
                                            transition: 'all 0.2s ease-in-out',
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1 }}>
                                            <Stack spacing={1} sx={{ flexGrow: 1 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5 }}>
                                                    <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.3, fontSize: '1.15rem' }}>
                                                        {planName ? t(planName) : '-'}
                                                    </Typography>
                                                    <Stack spacing={0.6} alignItems="flex-end">
                                                        {showTrialBadge && (
                                                            <Chip
                                                                size="small"
                                                                label={t('PROFILE_PLAN_TRIAL_AVAILABLE')}
                                                                sx={{
                                                                    backgroundColor: 'var(--button-background-color)',
                                                                    color: 'var(--button-text-color)',
                                                                    fontWeight: 600,
                                                                    height: 24,
                                                                    fontSize: '0.75rem',
                                                                }}
                                                            />
                                                        )}
                                                        {isCurrentPlan && (
                                                            <Chip
                                                                size="small"
                                                                label={t('PROFILE_PLAN_CURRENT')}
                                                                sx={{
                                                                    backgroundColor: 'var(--button-background-color)',
                                                                    color: 'var(--button-text-color)',
                                                                    fontWeight: 600,
                                                                    height: 24,
                                                                    fontSize: '0.75rem',
                                                                }}
                                                            />
                                                        )}
                                                    </Stack>
                                                </Box>

                                                <Box>
                                                    <Typography variant="h5" sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
                                                        {formatPlanPrice(plan, t('PROFILE_PLAN_PRICE_CONTACT'))}
                                                    </Typography>
                                                    {intervalLabel && (
                                                        <Typography variant="body2" sx={{ color: 'var(--link-text-color)', fontSize: '0.85rem', mt: 0.3 }}>
                                                            {t('PROFILE_PLANS_PER_LABEL')} {intervalLabel}
                                                        </Typography>
                                                    )}
                                                </Box>

                                                <Box
                                                    sx={{
                                                        px: 1.5,
                                                        py: 0.5,
                                                        borderRadius: 2,
                                                        backgroundColor: 'action.hover',
                                                        border: '1px solid var(--selected-table-row-color)',
                                                    }}
                                                >
                                                    <Typography variant="caption" sx={{ color: 'var(--link-text-color)', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                        {t('USER_MANAGEMENT_MODELS_ALLOWED_LABEL')}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.15 }}>
                                                        {formatModelsAllowed(plan.modelsAllowed, t('SUBSCRIPTION_MODELS_ALLOWED_UNLIMITED'))}
                                                    </Typography>
                                                </Box>

                                                {showAnyTrialInfo && (
                                                    <Box
                                                        sx={{
                                                            px: 1.5,
                                                            py: 0.5,
                                                            borderRadius: 2,
                                                            border: '1px solid var(--selected-table-row-color)',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.02)',
                                                        }}
                                                    >
                                                        <Stack spacing={0.8}>
                                                            {showTrialDuration && (
                                                                <Box>
                                                                    <Typography variant="caption" sx={{ color: 'var(--link-text-color)', fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                                                        {t('PROFILE_PLAN_TRIAL_DURATION')}
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                                                                        {formatTrialDuration(plan.trialDuration, t)}
                                                                    </Typography>
                                                                </Box>
                                                            )}

                                                            {showTrialWarning && (
                                                                <Typography
                                                                    variant="caption"
                                                                    sx={{
                                                                        color: 'var(--link-text-color)',
                                                                        fontSize: '0.75rem',
                                                                        lineHeight: 1.4,
                                                                    }}
                                                                >
                                                                    {t('PROFILE_PLAN_TRIAL_WARNING')}
                                                                </Typography>
                                                            )}
                                                        </Stack>
                                                    </Box>
                                                )}

                                                {isPendingCurrentPricePlan && (
                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            borderRadius: 2,
                                                            border: '1.5px solid var(--selected-table-row-color)',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                                        }}
                                                    >
                                                        <Stack spacing={0.8}>
                                                            <Chip
                                                                size="small"
                                                                label={t('PROFILE_PENDING_PRICE_CHANGE_TITLE')}
                                                                variant="outlined"
                                                                sx={{ alignSelf: 'flex-start', fontWeight: 700, height: 24, fontSize: '0.7rem' }}
                                                            />
                                                            <Typography variant="caption" sx={{ color: 'var(--link-text-color)', fontWeight: 600, fontSize: '0.7rem' }}>
                                                                {t('PROFILE_PENDING_PRICE_CHANGE_CURRENT_PRICE')}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: 'var(--link-text-color)', fontSize: '0.75rem', lineHeight: 1.4 }}>
                                                                {t('PROFILE_PLAN_PENDING_PRICE_CURRENT', {
                                                                    price: pendingTierPriceChange?.currentPriceDisplay || '-',
                                                                    date: pendingTierPriceChangeAppliesOnDateLabel || '-',
                                                                })}
                                                            </Typography>
                                                        </Stack>
                                                    </Box>
                                                )}

                                                {isPendingNextPricePlan && (
                                                    <Box
                                                        sx={{
                                                            p: 1.5,
                                                            borderRadius: 2,
                                                            border: '1.5px solid var(--selected-table-row-color)',
                                                            backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                                        }}
                                                    >
                                                        <Stack spacing={0.8}>
                                                            <Chip
                                                                size="small"
                                                                label={t('PROFILE_PENDING_PRICE_CHANGE_TITLE')}
                                                                variant="outlined"
                                                                sx={{ alignSelf: 'flex-start', fontWeight: 700, height: 24, fontSize: '0.7rem' }}
                                                            />
                                                            <Typography variant="caption" sx={{ color: 'var(--link-text-color)', fontWeight: 600, fontSize: '0.7rem' }}>
                                                                {t('PROFILE_PENDING_PRICE_CHANGE_NEXT_PRICE')}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: 'var(--link-text-color)', fontSize: '0.75rem', lineHeight: 1.4 }}>
                                                                {t('PROFILE_PLAN_PENDING_PRICE_NEXT', {
                                                                    price: pendingTierPriceChange?.nextPriceDisplay || '-',
                                                                    days: pendingTierPriceChangeAppliesInDays,
                                                                    date: pendingTierPriceChangeAppliesOnDateLabel || '-',
                                                                })}
                                                            </Typography>
                                                        </Stack>
                                                    </Box>
                                                )}
                                            </Stack>

                                            <Box sx={{ pt: 2 }}>
                                                {canManageInStripe && (
                                                    <Button
                                                        fullWidth
                                                        variant={canChangeInStripe ? 'outlined' : 'contained'}
                                                        disabled={isSubscriptionUiBusy || !plan.stripePriceId}
                                                        onClick={() => handleSelectPlan(plan, isCurrentPlan)}
                                                        className={!canChangeInStripe ? 'button-color' : undefined}
                                                        sx={{
                                                            fontWeight: 700,
                                                            minHeight: 44,
                                                            borderRadius: 2,
                                                            textTransform: 'none',
                                                            fontSize: '0.95rem',
                                                            borderWidth: canChangeInStripe ? 1.5 : undefined,
                                                            color: canChangeInStripe ? 'var(--button-background-color)' : undefined,
                                                            borderColor: canChangeInStripe ? 'var(--button-background-color)' : undefined,
                                                            boxShadow: !canChangeInStripe ? '0 2px 4px rgba(0, 0, 0, 0.1)' : undefined,
                                                            '&:hover': canChangeInStripe
                                                                ? {
                                                                    borderColor: 'var(--button-background-color)',
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                                                    borderWidth: 1.5,
                                                                }
                                                                : {
                                                                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                                                                },
                                                            '&.Mui-disabled': canChangeInStripe
                                                                ? {
                                                                    opacity: 0.7,
                                                                    cursor: 'not-allowed',
                                                                    boxShadow: 'none',
                                                                }
                                                                : {
                                                                    opacity: 0.72,
                                                                    cursor: 'not-allowed',
                                                                    boxShadow: 'none',
                                                                },
                                                        }}
                                                    >
                                                        {(uiAction?.type === 'plan' && uiAction.planId === plan.stripePriceId)
                                                            ? <CircularProgress size={20} color="inherit" />
                                                            : canChangeInStripe
                                                                ? t('PROFILE_CHANGE_BUTTON')
                                                                : showTrialBadge
                                                                    ? t('PROFILE_ACTIVATE_TRIAL_BUTTON')
                                                                    : t('PROFILE_BUY_BUTTON')}
                                                    </Button>
                                                )}
                                            </Box>
                                        </Box>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                )}
            </Box>
        </Card>
        <ConfirmDialog
            open={confirmDialog.open}
            title={t(confirmDialog.titleKey)}
            bodyText={confirmDialog.bodyKey ? t(confirmDialog.bodyKey, confirmDialog.bodyParams) : ''}
            isLoading={false}
            onClose={closeConfirmDialog}
            onConfirm={handleConfirmPlanDialog}
            cancelButtonText={t('CANCEL_BUTTON')}
            confirmButtonText={t('SUBMIT_BUTTON')}
            confirmColor="primary"
        />
        </>
    );
}